import React, { useState } from "react";
import { Chip, Mono, Sub, fmtD, fmtDT, LEG_LABEL, MS_TONE, CC_ICON, CC_TONE, SOURCE_LABEL } from "./ui";

const TH = "px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap";
const legTone = s => (s === "delivered" ? "good" : s === "exception" ? "bad" : ["notStarted", "planned"].includes(s) ? "muted" : "info");
const NODE_ROLE = { componentOrigin: "Components leave here", assembler: "2nd party · builds the pallet", customerDc: "Customer DC · goods receipt" };
const ASSEMBLY = { notStarted: "not started", inProgress: "in progress", complete: "complete", unknown: "unknown" };

// ── Journey A → B → C ───────────────────────────────────────────────────────
function JourneyStrip({ p, ry }) {
    const j = p.journey || {};
    const [ab, bc] = j.legs || [];
    const L = j.locations || {};
    const asm = j.assembly || {};
    const Node = ({ k, loc, extra }) => (
        <div className={`rounded-lg border p-3 ${j.currentNode === k ? "border-blue-500/60 bg-blue-500/5" : "border-[#1F2937] bg-[#0D1520]"}`}>
            <div className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-[#1F2937] text-[11px] font-bold text-white flex items-center justify-center shrink-0">{k}</span>
                <div className="min-w-0">
                    <b className="text-[12px] text-slate-100">{loc?.name || "—"}</b>
                    <Sub>{NODE_ROLE[loc?.role] || ""}{loc?.siteCode && <> · <Mono>{loc.siteCode}</Mono></>}</Sub>
                    {extra}
                </div>
            </div>
            {j.currentNode === k && <span className="inline-block mt-2 text-[10px] font-bold text-blue-300">Pallet is here</span>}
        </div>
    );
    const Leg = ({ lg, k }) => !lg ? null : (
        <div className={`rounded-lg border border-dashed p-3 space-y-1 ${j.currentNode === k ? "border-blue-500/60" : "border-[#1F2937]"}`}>
            <Chip tone={legTone(lg.status)}>{LEG_LABEL[lg.status] || lg.status}</Chip>
            <Sub>{lg.cargo === "components" ? "Components" : "Finished pallets"} · due {fmtD(lg.requiredDelivery, ry)}</Sub>
            {(lg.connotes || []).length > 0 && <Sub><Mono>{lg.connotes.join(", ")}</Mono></Sub>}
            {lg.carrierStatusText && <Sub>Carrier: “{lg.carrierStatusText}”</Sub>}
            <Sub>Source: {lg.statusSource === "outbound" ? "carrier report" : lg.statusSource === "palletProgram" ? "pallet sheet" : "no data yet"}</Sub>
        </div>
    );
    return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-stretch">
            <Node k="A" loc={L.A}/>
            <Leg lg={ab} k="A→B"/>
            <Node k="B" loc={L.B} extra={<Sub>Assembly: {ASSEMBLY[asm.status || "unknown"]}{asm.actualComplete ? ` · booked in ${fmtD(asm.actualComplete, ry)}` : asm.plannedComplete ? ` · due ${fmtD(asm.plannedComplete, ry)}` : ""}</Sub>}/>
            <Leg lg={bc} k="B→C"/>
            <Node k="C" loc={L.C}/>
        </div>
    );
}

export function JourneyTab({ p, ry }) {
    return (
        <div className="space-y-4">
            <JourneyStrip p={p} ry={ry}/>
            <div>
                <h4 className="text-[12px] font-bold text-white mb-2">Milestones</h4>
                <div className="border border-[#1F2937] rounded-lg overflow-x-auto">
                    <table className="w-full text-left text-[11px]">
                        <thead className="bg-[#0D1520]"><tr>{["Milestone", "Owner", "Planned", "Actual", "Status"].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                        <tbody className="divide-y divide-[#1F2937]">
                            {(p.milestones || []).map(m => (
                                <tr key={m.key} className="align-top">
                                    <td className="px-3 py-2 text-slate-200">{m.label}<Sub>{m.plannedSource || ""}</Sub></td>
                                    <td className="px-3 py-2 text-slate-400">{m.owner}</td>
                                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtD(m.planned, ry)}</td>
                                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">
                                        {m.actual ? fmtD(m.actual, ry) : m.done ? "✓" : "—"}
                                        {m.dateQuality === "suspect" && <> <Chip tone="warn" title="Date looks wrong in the sheet">check</Chip></>}
                                    </td>
                                    <td className="px-3 py-2">
                                        <Chip tone={MS_TONE[m.status]}>{m.status === "overdue" ? `${m.daysLate}d overdue` : m.status === "dueSoon" ? "due soon" : m.status}</Chip>
                                        {m.evidence && m.status === "done" && <Sub>{m.evidence}</Sub>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── BOM & cross-checks ──────────────────────────────────────────────────────
const checkName = id => id.replace(/_/g, " ").toLowerCase().replace(/^./, x => x.toUpperCase());
const CHECK_ORDER = { fail: 0, warn: 1, pass: 2, notApplicable: 3 };
const CHECK_COLOR = { good: "text-green-400", bad: "text-red-400", warn: "text-amber-400", muted: "text-slate-500" };

function CheckRow({ c, small }) {
    const tone = CC_TONE[c.result];
    return (
        <li className="flex gap-2">
            <span className={`w-4 text-center font-bold ${CHECK_COLOR[tone]}`} aria-label={c.result}>{CC_ICON[c.result]}</span>
            <div className="min-w-0">
                {!small && <b className="text-[11px] text-slate-200">{checkName(c.id)}</b>}
                <div className="text-[11px] text-slate-300">{c.message}</div>
                {!small && c.sources && <Sub>{c.sources.map(s => SOURCE_LABEL[s] || s).join(" ↔ ")}</Sub>}
            </div>
        </li>
    );
}

export function BomTab({ p, lines, ry }) {
    const [open, setOpen] = useState(null);
    const checks = [...(p.crossChecks || [])].sort((a, b) => CHECK_ORDER[a.result] - CHECK_ORDER[b.result]);
    return (
        <div className="space-y-4">
            <div>
                <h4 className="text-[12px] font-bold text-white mb-2">Cross-checks against current files</h4>
                <ul className="space-y-2">{checks.map((c, i) => <CheckRow key={c.id + i} c={c}/>)}</ul>
            </div>
            <div>
                <h4 className="text-[12px] font-bold text-white mb-2">
                    BOM · {p.bom?.components} components × {p.palletQty} pallets = {(p.bom?.totalUnits || 0).toLocaleString()} units
                </h4>
                <div className="border border-[#1F2937] rounded-lg overflow-x-auto">
                    <table className="w-full text-left text-[11px]">
                        <thead className="bg-[#0D1520]"><tr>{["SKU", "Per pallet", "Total", "Supply", "Reserved", "Inbound link", "Checks"].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
                        <tbody className="divide-y divide-[#1F2937]">
                            {lines.map(l => {
                                const hi = l.inbound.find(x => x.conf === "high");
                                const issues = l.crossChecks.filter(c => c.result === "fail" || c.result === "warn");
                                return (
                                    <React.Fragment key={l.id}>
                                        <tr onClick={() => setOpen(open === l.id ? null : l.id)} aria-expanded={open === l.id} className="hover:bg-[#1F2937]/40 cursor-pointer align-top">
                                            <td className="px-3 py-2"><span className="font-mono text-slate-200">{l.sku}</span><Sub>{l.description}</Sub></td>
                                            <td className="px-3 py-2 tabular-nums text-slate-300">{l.qtyPerPdq}</td>
                                            <td className="px-3 py-2 tabular-nums text-slate-300">{l.totalQty.toLocaleString()}</td>
                                            <td className="px-3 py-2"><Chip tone={l.supply.risk === "Secured" ? "good" : l.supply.risk ? "bad" : "muted"}>{l.supply.risk || "—"}</Chip></td>
                                            <td className="px-3 py-2 tabular-nums text-slate-300">{(l.supply.reserveQtyTotal || 0).toLocaleString()}</td>
                                            <td className="px-3 py-2">
                                                {hi ? <><span className="font-mono text-slate-200">{hi.containerNumber || hi.id.split("-").slice(1, 3).join("/")}</span><Sub>{hi.status} · ERD {fmtD(hi.erd, ry)}</Sub></>
                                                    : l.inbound.length ? <Sub>{l.inbound.length} possible</Sub> : <Sub>from stock</Sub>}
                                            </td>
                                            <td className="px-3 py-2">{issues.length
                                                ? <Chip tone={issues.some(f => f.result === "fail") ? "bad" : "warn"}>{issues.length} issue{issues.length > 1 ? "s" : ""}</Chip>
                                                : <Chip tone="good">ok</Chip>}</td>
                                        </tr>
                                        {open === l.id && (
                                            <tr className="bg-[#0D1520]"><td colSpan={7} className="px-3 py-3 space-y-2">
                                                {l.supply.supplyPlanningUpdate && <p className="text-[11px] text-slate-300"><b>Supply planning update:</b> {l.supply.supplyPlanningUpdate}</p>}
                                                <ul className="space-y-1">{l.crossChecks.map(c => <CheckRow key={c.id} c={c} small/>)}</ul>
                                                {l.inbound.length > 0 && <Sub>Inbound: {l.inbound.map(x => `${x.id} (${x.conf}, ${x.status || "—"}, ERD ${fmtD(x.erd, ry)})`).join(" · ")}</Sub>}
                                                <Sub>Sheet row {l.row ?? "—"} · {l.progress?.completedSteps ?? 0}/33 steps · next owner {l.progress?.currentOwner || "—"}</Sub>
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── Timeline ────────────────────────────────────────────────────────────────
const EV_GROUP = { MILESTONE_OVERDUE: "overdue", MILESTONE_DUE_SOON: "overdue", CROSSCHECK_FAILED: "checks", CROSSCHECK_RESOLVED: "checks",
    HANDOFF_SENT: "handoffs", COMMENT_ADDED: "handoffs", NOTIFICATION_SENT: "handoffs" };
const FILTERS = [["all", "All"], ["overdue", "Dates"], ["checks", "Checks"], ["handoffs", "Hand-offs & comments"], ["changes", "Changes"]];
const SEV_DOT = { error: "#EF4444", warning: "#F59E0B", success: "#10B981", info: "#3B82F6" };
const SOURCE_KIND = { dateRule: "date rule", upload: "upload", system: "system" };

export function TimelineTab({ events, ry }) {
    const [f, setF] = useState("all");
    const list = events.filter(e => f === "all" || (EV_GROUP[e.type] || "changes") === f);
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-1" role="tablist" aria-label="Filter events">
                {FILTERS.map(([k, l]) => (
                    <button key={k} role="tab" aria-selected={f === k} onClick={() => setF(k)}
                        className={`px-2.5 py-1 rounded-lg text-[11px] border ${f === k ? "bg-blue-500/20 border-blue-500/40 text-blue-300" : "border-[#1F2937] text-slate-400 hover:border-slate-600"}`}>{l}</button>
                ))}
            </div>
            <ol className="relative border-l border-[#1F2937] ml-1.5 space-y-3">
                {list.map(e => (
                    <li key={e.id} className="pl-4">
                        <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: SEV_DOT[e.severity] || "#6B7280" }}/>
                        <div className="text-[12px] text-slate-200">{e.title}</div>
                        {e.detail && <div className="text-[11px] text-slate-400 whitespace-pre-wrap">{e.detail}</div>}
                        <Sub>
                            {e.source?.kind === "user" ? `${fmtDT(e.occurredAt)} · ${e.source.by?.name || "user"}` : `${fmtD(e.occurredAt, ry)} · ${SOURCE_KIND[e.source?.kind] || "upload"}`}
                            {e.notify ? " · email queued" : ""}
                            {e.handoff ? ` · to ${e.handoff.to.join(", ")}${e.handoff.cc?.length ? ` · cc ${e.handoff.cc.join(", ")}` : ""}` : ""}
                        </Sub>
                    </li>
                ))}
                {list.length === 0 && <li className="pl-4 text-[11px] text-slate-500">No events in this filter.</li>}
            </ol>
        </div>
    );
}
